import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, BadgeCheck, Building2, Recycle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { FileField } from "@/components/FileField";
import { EnablingDocs } from "@/components/EnablingDocs";
import { GESTOR_GIROS, GESTOR_STATUS, validRut } from "@/lib/ops";
import { errMsg, useApi } from "@/lib/api";

const EMPTY = { rut: "", razon_social: "", giro: "", direccion: "", tons_generated_year: "", is_ppp: false, gestor: false, gestor_giros: [], retc_id: "", resolucion_numero: "", resolucion_vence: "", resolucion_file: null, rep_certs: [], sma_declaration: false };

const F = ({ label, children, hint }) => (
  <div className="space-y-2"><Label className="text-slate-700">{label}</Label>{children}{hint && <p className="text-xs text-slate-500">{hint}</p>}</div>
);

const Card = ({ icon: Icon, title, children, testId }) => (
  <section className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-6" data-testid={testId}>
    <h2 className="flex items-center gap-2 font-semibold text-lg text-[#05130e]"><Icon className="w-5 h-5 text-[#00b37a]" />{title}</h2>
    {children}
  </section>
);

const GestorSection = ({ f, set, status }) => (
  <Card icon={Recycle} title="Protocolo de Verificación de Gestor / Transformador" testId="gestor-section">
    <div className="flex items-center justify-between gap-4">
      <p className="text-sm text-slate-600">Actívalo si tu empresa compra o recibe material para valorizarlo. Es requisito para que la transacción sea válida ante la Ley REP.</p>
      <Switch checked={f.gestor} onCheckedChange={(v) => set("gestor", v)} data-testid="gestor-switch" />
    </div>
    {f.gestor && (
      <div className="space-y-6">
        <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full border ${GESTOR_STATUS[status].cls}`} data-testid="gestor-status-badge">{GESTOR_STATUS[status].label}</span>
        <F label="1. Giros comerciales registrados en el SII (marca los que apliquen)">
          <div className="grid sm:grid-cols-2 gap-3">
            {GESTOR_GIROS.map((g) => (
              <label key={g} className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={f.gestor_giros.includes(g)} onCheckedChange={() => set("gestor_giros", f.gestor_giros.includes(g) ? f.gestor_giros.filter((x) => x !== g) : [...f.gestor_giros, g])} data-testid={`gestor-giro-${GESTOR_GIROS.indexOf(g)}`} />{g}
              </label>
            ))}
          </div>
        </F>
        <F label="2. ID de Establecimiento RETC (Ventanilla Única)" hint="Permite cruzar la información con las bases públicas del Ministerio del Medio Ambiente.">
          <Input className="h-11 font-mono" value={f.retc_id} onChange={(e) => set("retc_id", e.target.value)} placeholder="Ej: 1234567" data-testid="gestor-retc-input" />
        </F>
        <div className="grid sm:grid-cols-2 gap-5">
          <F label="3. N° Resolución Sanitaria (Seremi de Salud)"><Input className="h-11" value={f.resolucion_numero} onChange={(e) => set("resolucion_numero", e.target.value)} data-testid="gestor-resolucion-number-input" /></F>
          <F label="Vigente hasta"><Input type="date" className="h-11" value={f.resolucion_vence || ""} onChange={(e) => set("resolucion_vence", e.target.value)} data-testid="gestor-resolucion-date-input" /></F>
        </div>
        <F label="Copia de la Resolución Sanitaria vigente (PDF)"><FileField value={f.resolucion_file} onChange={(v) => set("resolucion_file", v)} testId="gestor-resolucion-file" label="Subir resolución" /></F>
        <F label="Certificados de acreditación REP (si aplica: aceites, baterías, etc.)"><FileField multiple value={f.rep_certs} onChange={(v) => set("rep_certs", v)} testId="gestor-rep-files" label="Subir certificados" /></F>
        <label className="flex items-start gap-3 text-sm text-slate-700 bg-[#f4f7f6] rounded-xl p-4">
          <Checkbox checked={f.sma_declaration} onCheckedChange={(v) => set("sma_declaration", !!v)} className="mt-0.5" data-testid="gestor-sma-checkbox" />
          4. Declaro que la empresa no tiene sanciones activas registradas en la Superintendencia del Medio Ambiente (SMA) y que la información entregada es verídica. Esta declaración se renueva anualmente.
        </label>
        <p className="flex items-start gap-2 text-xs text-slate-500"><AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />La validación automática de RUT y giros contra el SII y de sanciones contra la SMA aún no está conectada: hoy se valida el formato del RUT y se registra la declaración propia con sus documentos.</p>
      </div>
    )}
  </Card>
);

export default function Profile() {
  const api = useApi();
  const [f, setF] = useState(EMPTY);
  const [status, setStatus] = useState("no_declarado");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api("get", "/me/company").then(({ data }) => {
      setF((s) => ({ ...s, ...Object.fromEntries(Object.entries(data).filter(([k, v]) => k in EMPTY && v !== null)) }));
      setStatus(data.gestor_status);
    }).catch(() => {});
  }, [api]);

  const save = async () => {
    if (!validRut(f.rut)) return toast.error("RUT inválido: revisa el dígito verificador");
    setBusy(true);
    try {
      const body = { ...f, tons_generated_year: f.tons_generated_year === "" ? null : Number(f.tons_generated_year), resolucion_vence: f.resolucion_vence || null };
      const { data } = await api("put", "/me/company", body);
      setF((s) => ({ ...s, rut: data.rut }));
      setStatus(data.gestor_status);
      toast.success("Perfil de empresa guardado");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-4xl mx-auto px-5 lg:px-8 py-12 space-y-6" data-testid="profile-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00b37a] mb-2">Mi empresa</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#05130e]">Perfil tributario y ambiental</h1>
        <p className="text-slate-600 mt-2">Estos datos se usan en las Órdenes de Retiro, la cadena de custodia, el respaldo SINADER y los certificados.</p>
      </div>
      <EnablingDocs />
      <Card icon={Building2} title="Identificación de la empresa" testId="company-section">
        <div className="grid sm:grid-cols-2 gap-5">
          <F label="RUT empresa"><Input className="h-11 font-mono" value={f.rut} onChange={(e) => set("rut", e.target.value)} placeholder="76.123.456-7" data-testid="company-rut-input" /></F>
          <F label="Razón social"><Input className="h-11" value={f.razon_social} onChange={(e) => set("razon_social", e.target.value)} data-testid="company-name-input" /></F>
          <F label="Giro principal"><Input className="h-11" value={f.giro} onChange={(e) => set("giro", e.target.value)} data-testid="company-giro-input" /></F>
          <F label="Dirección de planta"><Input className="h-11" value={f.direccion} onChange={(e) => set("direccion", e.target.value)} data-testid="company-address-input" /></F>
          <F label="Residuos generados al año (t)" hint="Se usa para calcular tu tasa de desvío de relleno sanitario."><Input type="number" min="0" className="h-11 font-mono" value={f.tons_generated_year} onChange={(e) => set("tons_generated_year", e.target.value)} data-testid="company-tons-input" /></F>
        </div>
        <label className="flex items-center gap-3 text-sm text-slate-700">
          <Checkbox checked={f.is_ppp} onCheckedChange={(v) => set("is_ppp", !!v)} data-testid="company-ppp-checkbox" />Somos Productor de Productos Prioritarios (PPP) bajo la Ley REP 20.920
        </label>
      </Card>
      <GestorSection f={f} set={set} status={status} />
      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="h-12 px-8 rounded-full font-semibold" data-testid="profile-save-btn"><BadgeCheck className="w-4 h-4" />{busy ? "Guardando..." : "Guardar perfil"}</Button>
      </div>
    </main>
  );
}
